import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { navigate } from '@reach/router';
import {
  Grid,
  makeStyles,
  withStyles,
  Paper,
  Theme,
  IconButton,
  Typography,
  Hidden,
  Button,
  Divider,
  Tooltip,
} from '@material-ui/core';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { STORAGE_URL } from 'commons/constants/KeyFlowConstants';
import { AssetProps } from 'components/AssetThumbnailView/type';
import CategoryVO from 'commons/vo/CategoryVO';
import { useInit } from 'hooks';
import StorageVO from 'commons/vo/StorageVO';
import AssetVO from 'commons/vo/AssetVO';
import defaultThumbnail from 'asset/thumbnail/thumb_default.jpg';
import { WkblScriptThumbnailProps } from 'components/ScriptListView/type';
import WkblScriptVO from 'commons/vo/WkblScriptVO';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/reducers/type';
import CodeVO from 'commons/vo/CodeVO';
import TimeCode from 'commons/utils/TimeCode';

import { VersusIcon, VersusMobileIcon } from 'asset/images';
import { getDayOfWeek } from 'commons/utils/DateUtils';

const useStyles = makeStyles((theme: Theme) => ({
  assetItem: {
    // 기존의 계산식 85px - 16px(spacing 2) = 69px
    // 기존의 계산식이 어떤걸 의미하는지는 모르겠으나 변경해도 문제 없는것 같아 요청사항 대로 변경
    marginBottom: '35px',
    [theme.breakpoints.down('md')]: {
      marginBottom: '35px',
    },
  },
  paper: {
    height: '307px',
    boxShadow: theme.shadows[0],
    [theme.breakpoints.down('md')]: {
      height: '265px',
    },
  },
  imgContainer: {
    // [Skim 의 스크러빙 로직 때문에 높이를 지정하면 안됨]
    // - skim 이미지 비율 16:9 이미지가 50장이 세로로 이어 붙임
    // - 때문에 width의 56.25% 의 height 를 가져가야 함
    position: 'relative',
    backgroundOrigin: 'content-box',
    backgroundSize: 'cover',
    paddingTop: '56.25%',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      // 모바일에서 skim 이 동작하지 않는다면 영역 크기 문제로 height 를 지정해주거나 최대 너비등을 지정해야함.
      minWidth: '209px',
      minHeight: '185px',
      paddingTop: 0,
      backgroundPosition: 'center !important',
    },
  },
  img: {
    width: '100%',
  },
  scrubbingBar: {
    position: 'absolute',
    top: 0,
    width: 0,
    height: '100%',
    border: '0.8px solid rgba(255, 30, 1, 0.8)',
  },
  assetContainer: {
    height: '82px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  assetContentContainer: {
    width: '100%',
    alignSelf: 'flex-end',
    [theme.breakpoints.down('md')]: {
      width: 'auto',
      minWidth: 0,
      flexGrow: 1,
      alignSelf: 'auto',
      display: 'flex',
      flexWrap: 'wrap',
    },
  },
  scriptTitleContainer: {
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '14px',
  },
  assetTitleContainer: {
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '16px',
    color: '#000',
    [theme.breakpoints.down('md')]: {
      width: 'auto',
      flex: '1 1 100%',
      paddingRight: '5px',
      fontSize: '14px',
    },
  },
  assetDateContainer: {
    width: '100%',
    fontSize: '14px',
    color: '#999',
    [theme.breakpoints.down('md')]: {
      width: 'auto',
      flex: '1 1 100%',
      paddingRight: '5px',
      fontSize: '11px',
    },
  },
  menuButton: {
    width: '100px',
    height: '36px',
    border: '1px solid #b6b6b6',
    borderRadius: 0,
    backgroundColor: 'transparent',
    fontSize: '14px',
    [theme.breakpoints.down('md')]: {
      flexBasis: '60px',
      flexShrink: 0,
      flexGrow: 0,
      fontSize: '14px',
    },
  },
  typeContainer: {
    width: '112px',
    height: '27px',
    position: 'absolute',
    padding: '2px',
    zIndex: 100,
    top: 0,
    right: 0,
    fontSize: '14px',
    textAlign: 'center',
    color: '#FFF',
    backgroundColor: '#D94F98',
    [theme.breakpoints.down('md')]: {
      width: 'auto',
      height: '20px',
      padding: '0 10px',
      fontSize: '12px',
    },
  },
  centerLine: {
    border: '1px solid gray',
    position: 'absolute',
    left: '50%',
    width: 0,
    height: '100%',
    display: 'inline-block',
  },
  teamContainer: {
    position: 'absolute',
    top: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    overflow: 'hidden',
  },
  teamLogoContainer: {
    width: '220px',
    height: '110px',
    position: 'relative',
    marginTop: '54px',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 1.0)',
    [theme.breakpoints.down('md')]: {
      width: '171px',
      height: '85px',
      marginTop: '40px',
    },
  },
  teamBorder: {
    backgroundColor: '#000',
  },
  team: {
    position: 'relative',
    width: '50%',
    padding: '20px',
    '& > img': {
      width: '100%',
      height: '100%',
    },
  },
  leftBorder: {
    borderLeft: '0.5px solid gray',
  },
  verseTextContainer: {
    position: 'absolute',
    zIndex: 1,
    left: 'calc(50% - 15px)',
    width: '30px',
    lineHeight: 2.0,
    borderRadius: '50%',
    backgroundColor: 'rgba(202, 90, 150, 1.0)',
    color: 'rgba(255, 255, 255, 1.0)',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  teamNameContainer: {
    width: '220px',
    height: '27px',
    display: 'flex',
    margin: 'auto',
    justifyContent: 'space-around',
    backgroundColor: '#999',
    '& > span': {
      padding: '2px',
      flexBasis: '50%',
      fontSize: '14px',
      color: 'rgba(255, 255, 255, 1.0)',
      textAlign: 'center',
      overflow: 'hidden',
    },
    [theme.breakpoints.down('md')]: {
      width: '171px',
      height: '20px',
      '& > span': {
        padding: 0,
        flexBasis: '50%',
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 1.0)',
        textAlign: 'center',
        overflow: 'hidden',
      },
    },
  },
}));

const HtmlTooltip = withStyles((theme: Theme) => ({
  tooltip: {
    backgroundColor: '#FFF',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(14),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

function WkblScript({ script }: WkblScriptThumbnailProps) {
  const classes = useStyles();

  const { kfScriptRefRangeList: rangeTypeList } = useSelector((state: RootState) => state.code);

  const [inTimecodeUtil, setInTimecodeUtil] = useState<TimeCode>(() => {
    const timecodeUtil = new TimeCode(script.mediaFps);
    timecodeUtil.setFrames(script.inPoint);
    return timecodeUtil;
  });

  const [outTimecodeUtil, setOutTimecodeUtil] = useState<TimeCode>(() => {
    const timecodeUtil = new TimeCode(script.mediaFps);
    timecodeUtil.setFrames(script.outPoint);
    return timecodeUtil;
  });

  const [skimPoisitionPercent, setSkimPositionPercent] = useState<number>(100);
  const [scrubbingPercent, setScrubbingPercent] = useState<number>(0);
  const [isShowScrubbingBar, setIsShowScrubbingBar] = useState<boolean>(false);

  const symbolicPath: string = useMemo(() => {
    return script.symbolicPath;
  }, [script.symbolicPath]);

  const backgroundImage = useMemo(() => script.getBackgroundImage(), [script]);

  const teamName = useMemo(() => {
    const { team1Name, team2Name } = script;
    const team1 = team1Name ? team1Name.split(' ')[0] : '';
    const team2 = team2Name ? team2Name.split(' ')[0] : '';

    return [team1, team2];
  }, [script.team1Name, script.team2Name]);

  const team1LogoPath = useMemo(() => {
    return script.getTeam1LogoPath(symbolicPath);
  }, [script, symbolicPath]);

  const team2LogoPath = useMemo(() => {
    return script.getTeam2LogoPath(symbolicPath);
  }, [script, symbolicPath]);

  const isShowTeamContainer = useMemo(() => {
    return script.team1 && script.team2;
  }, [script]);

  const { team1, team2 } = useMemo(() => {
    return script.getTeamLogoPath();
  }, [script]);

  const rangeTypeName = useMemo(() => {
    const rangeTypeItem: CodeVO[] =
      rangeTypeList.filter((item: CodeVO) => item.codevalue === script.range) || [];

    if (rangeTypeItem.length > 0) {
      return rangeTypeItem[0].name || null;
    }
    return null;
  }, [rangeTypeList, script.range]);

  const inTimecode = useMemo(() => {
    return inTimecodeUtil.getTimeCode();
  }, [inTimecodeUtil]);

  const outTimecode = useMemo(() => {
    return outTimecodeUtil.getTimeCode();
  }, [outTimecodeUtil]);

  const onMouseEnterThumbnail = useCallback(() => {
    setIsShowScrubbingBar(true);
  }, []);

  const onMouseLeaveThumbnail = useCallback(() => {
    setIsShowScrubbingBar(false);
  }, []);

  const onPopupAssetDetail = useCallback(() => {
    const inTime = inTimecodeUtil.getSeconds();
    const outTime = outTimecodeUtil.getSeconds();
    // const inTime = Math.round(inTimecodeUtil.getSeconds());
    // const outTime = Math.round(outTimecodeUtil.getSeconds());
    const encodedKeyword = encodeURIComponent(script.script);
    window.open(`/preview/${script.videoId}?in=${inTime}&out=${outTime}&keyword=${encodedKeyword}`);
  }, [script, inTimecodeUtil, outTimecodeUtil]);

  return (
    <Grid item xs={12} lg={4} key={script.id} className={classes.assetItem}>
      <Paper elevation={2} className={clsx(classes.paper)}>
        <div
          className={clsx(classes.imgContainer)}
          style={{
            backgroundImage,
          }}
          onMouseEnter={onMouseEnterThumbnail}
          onMouseLeave={onMouseLeaveThumbnail}
          onClick={onPopupAssetDetail}
        >
          {isShowTeamContainer && (
            <div
              className={classes.teamContainer}
              style={{
                // display: !isShowScrubbingBar && isShowTeamContainer ? 'inline-block' : 'none',
                display: !isShowScrubbingBar ? 'inline-block' : 'none',
              }}
            >
              <div className={classes.teamLogoContainer}>
                {/* 홈 팀 */}
                <span className={clsx(classes.team)}>
                  <img src={team1.db}></img>
                </span>
                <Divider className={classes.teamBorder} orientation="vertical" />
                <img className={classes.verseTextContainer} src={VersusIcon} />
                {/* 어웨이 팀 */}
                <span className={classes.team}>
                  <img src={team2.db}></img>
                </span>
              </div>
              <div className={classes.teamNameContainer}>
                {/* 홈 팀 */}
                <span style={{ backgroundColor: script.team1Color }}>{teamName[0]}</span>
                <Divider className={classes.teamBorder} orientation="vertical" />
                {/* 어웨이 팀 */}
                <span style={{ backgroundColor: script.team2Color }}>{teamName[1]}</span>
              </div>
            </div>
          )}
          {rangeTypeName && (
            <Typography className={classes.typeContainer}>{rangeTypeName}</Typography>
          )}
        </div>
        <div className={clsx(classes.assetContainer)}>
          <div className={classes.assetContentContainer}>
            <div className={clsx(classes.assetTitleContainer)}>{script.script}</div>
            <div className={clsx(classes.scriptTitleContainer)}>
              {inTimecode} ~ {outTimecode}
            </div>
            <div className={clsx(classes.scriptTitleContainer)}>{script.title}</div>
            <div className={clsx(classes.assetDateContainer)}>
              {script.formattedGameDate} ({getDayOfWeek(script.formattedGameDate)})
            </div>
          </div>
        </div>
      </Paper>
    </Grid>
  );
}

export default WkblScript;
